import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import isNil from 'lodash/isNil';

import {
  getScheduleTimeFormatted,
  getCloseTimeFormatted,
  getAddedToQueueDateFormatted,
} from 'shared/utils/dateTimeUtils';
import { APPOINTMENT_STATUS_TITLES } from 'modules/queues/constants/appointmentStatusTitles';
import { SCHEDULED, WALK_IN } from 'modules/queues/constants/queueTypes';
import CaseDataForm from '../CaseDataForm';
import {
  DetailsInfoRow,
  ServiceInfoRow,
  ProviderInfoRow,
  CustomerPatientInfoRow,
  SubjectInfoRow,
  AppointmentStatusInfoRow,
} from './components';

import theme from './AppointmentDetails.scss';
import { HOME_VISIT } from '../../../queues/constants/F2FTypes';


const propTypes = {
  service: PropTypes.object,
  subject: PropTypes.object,
  customer: PropTypes.object,
  provider: PropTypes.object,
  assignedToGroup: PropTypes.bool,
  isF2F: PropTypes.bool.isRequired,
  isClosed: PropTypes.bool.isRequired,
  closeReason: PropTypes.string.isRequired,
  isCustomerPatient: PropTypes.bool.isRequired,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  closeTime: PropTypes.string,
  externalId: PropTypes.string,
  missingFilesMessage: PropTypes.string,
  missingCaseMessage: PropTypes.string,
  queueType: PropTypes.oneOf([SCHEDULED, WALK_IN]).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    canSeeProvider: PropTypes.bool.isRequired,
    canSeeCustomer: PropTypes.bool.isRequired,
    canSeeSubjects: PropTypes.bool.isRequired,
    canSeeSchedule: PropTypes.bool.isRequired,
    canSeeStatus: PropTypes.bool.isRequired,
    canSeeCaseSummary: PropTypes.bool.isRequired,
  }).isRequired,
  providerGroupSpecialty: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
};

const defaultProps = {
  closeTime: null,
  assignedToGroup: false,
  service: null,
  provider: null,
  providerGroupSpecialty: null,
  customer: null,
  subject: null,
  startTime: null,
  endTime: null,
  externalId: null,
  missingFilesMessage: null,
  missingCaseMessage: null,
};

const AppointmentDetails = ({
  intl,
  style,
  isF2F,
  service,
  endTime,
  subject,
  customer,
  isClosed,
  provider,
  providerGroupSpecialty,
  startTime,
  closeTime,
  queueType,
  externalId,
  permissions,
  closeReason,
  faceToFaceType,
  assignedToGroup,
  isCustomerPatient,
  missingCaseMessage,
  missingFilesMessage,
}) => (
  <div className={ theme.root } style={ style }>
    <div className={ theme.container }>

      <If condition={ queueType === WALK_IN }>
        <div className={ theme.unscheduledLabel }>
          <h3>{ intl.formatMessage({ id: 'Appointment.View.Indicator.WalkInAppointment', defaultMessage: '** Walk-in Appointment' }) }</h3>
        </div>
      </If>
      <If condition={ missingFilesMessage }>
        <div className={ theme.missingFilesMessage }> { missingFilesMessage } </div>
      </If>
      <If condition={ missingCaseMessage }>
        <div className={ theme.missingFilesMessage }> { missingCaseMessage } </div>
      </If>

      <If condition={ permissions.canSeeCaseSummary }>
        <CaseDataForm />
      </If>

      <If condition={ service }>
        <ServiceInfoRow { ...{ ...service, isDisabled: isClosed } } />
      </If>

      <If condition={ assignedToGroup || provider || providerGroupSpecialty }>
        <ProviderInfoRow
          { ...{ provider, assignedToGroup, providerGroupSpecialty, permissions, isClosed } }
        />
      </If>

      <If condition={ customer }>
        <Choose>
          <When condition={ isCustomerPatient }>
            <CustomerPatientInfoRow { ...{ customer, intl, permissions, isDisabled: isClosed } } />
          </When>
          <Otherwise>
            <DetailsInfoRow
              withBorder
              isMasked={ !permissions.canSeeCustomer }
              isDisabled={ isClosed }
              initials={ customer.initials }
              avatar={ customer.avatar }
              title={ <FormattedMessage id="Appointment.View.Details.Customer.Title" defaultMessage="** Customer" /> }
              description={ customer.name }
            />
          </Otherwise>
        </Choose>
      </If>

      <If condition={ subject }>
        <SubjectInfoRow { ...{ subject, permissions, isDisabled: isClosed } } />
      </If>

      <Choose>
        <When condition={ queueType === SCHEDULED }>
          <DetailsInfoRow
            withBorder
            isDisabled={ isClosed }
            isMasked={ !permissions.canSeeSchedule }
            iconName={ isF2F ? faceToFaceType === HOME_VISIT ? 'home' : 'two-people' : 'calendar' }
            title={ <FormattedMessage id="Appointment.View.Details.Schedule.Title" defaultMessage="** Schedule" /> }
            description={ getScheduleTimeFormatted(startTime, endTime, intl) }
          />
        </When>
        <When condition={ queueType === WALK_IN }>
          <DetailsInfoRow
            withBorder
            isDisabled={ isClosed }
            isMasked={ !permissions.canSeeSchedule }
            iconName={ isF2F ? faceToFaceType === HOME_VISIT ? 'home' : 'two-people' : 'characters-shrink' }
            title={ <FormattedMessage
              id="Appointment.View.Details.WalkIn.Title"
              defaultMessage="** Added to queue on:"
            /> }
            description={ getAddedToQueueDateFormatted(startTime, intl) }
          />
        </When>
      </Choose>

      <If condition={ isClosed && !isNil(closeTime) }>
        <AppointmentStatusInfoRow
          withBorder
          insidePanel
          isDisabled={ isClosed }
          iconName="check"
          isMasked={ !permissions.canSeeStatus }
          isIconHide={ !permissions.canSeeStatus }
          title={ <FormattedMessage id="Appointment.View.Details.ClosedTime.Title" defaultMessage="** Closed" /> }
          description={ getCloseTimeFormatted(closeTime, intl) }
          additionalInfo={ intl.formatMessage(APPOINTMENT_STATUS_TITLES[closeReason].title) }
        />
      </If>

      <If condition={ externalId }>
        <DetailsInfoRow
          withBorder
          isDisabled={ isClosed }
          iconName="appointment-id"
          title={ <FormattedMessage
            id="Appointment.View.Details.AppointmentID.Title"
            defaultMessage="** Appointment ID"
          /> }
          description={ externalId }
        />
      </If>
    </div>
  </div>
);

AppointmentDetails.propTypes = propTypes;
AppointmentDetails.defaultProps = defaultProps;

export default injectIntl(AppointmentDetails);
